import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Box, Grid } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import { imageUpload } from "Apis/signIn";
import { LoginFormContext } from "context/LoginFormContext";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import colors from "assets/theme/base/colors";

const ImageUploader = ({
  label,
  img,
  folderName,
  fileName,
  formik,
  hideLabel,
  required,
  name,
  setServiceProfile,
  serviceProfile,
  index,
  note = "",
}) => {
  const { setProfileImage } = useContext(LoginFormContext);
  const [selectedFile, setSelectedFile] = useState(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [imageName, setImageName] = useState("");
  const [isFileTooLarge, setIsFileTooLarge] = useState(false);
  const { imageUploader, info, sideNav, error } = colors;
  const [validationMessage, setValidationMessage] = useState("");

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setIsFileTooLarge(true);
        setValidationMessage("Image can't be larger than 5 MB");
        setSelectedFile(null);
        formik.setFieldValue(fileName, null);
        return;
      }
      setIsFileTooLarge(false);
      setSelectedFile(file);
      setImageName(file.name);
      setHasUploaded(true);
      setValidationMessage("");

      const formData = new FormData();
      formData.append("folderName", folderName);
      formData.append("file", file);

      let fileUploadResponse = await imageUpload(formData);
      if (fileUploadResponse?.status === 200) {
        const imageUrl = fileUploadResponse?.data.data[0].url;
        const uploadedImageName = fileUploadResponse?.data.data[0].name;

        if (setServiceProfile && serviceProfile) {
          setServiceProfile((prev) => {
            if (Array.isArray(prev)) {
              return prev.map((item, imgIndex) => {
                if (imgIndex === index) {
                  return { ...item, url: imageUrl, name: uploadedImageName };
                }
                return item;
              });
            }
            return []; // Return empty array if prev is not an array
          });
        } else {
          setProfileImage({ url: imageUrl, name: uploadedImageName });
        }
        formik.setFieldValue(fileName, uploadedImageName);
        localStorage.setItem(
          "profileImage",
          JSON.stringify({ name: uploadedImageName, url: imageUrl })
        );
      }
    }
  };

  const handleImageRemove = () => {
    formik.setFieldValue(name, null);
    if (setServiceProfile && serviceProfile) {
      setServiceProfile((prev) => {
        return prev.map((item, imgIndex) => {
          if (imgIndex === index) {
            return { ...item, url: null };
          }
          return item;
        });
      });
    } else {
      setProfileImage({ url: null, name: null });
    }
    localStorage.removeItem("profileImage");
    setImageName(folderName);
    setSelectedFile(null);
    setHasUploaded(false);
    setValidationMessage("");
  };

  return (
    <Grid container direction="column" alignItems="start">
      {!hideLabel && (
        <MDBox
          sx={{
            fontSize: "16px",
            fontWeight: "400",
            marginBottom: "5px",
            marginTop: `${hideLabel ? "0px" : "10px"}`,
            textAlign: "start",
            width: "100%",
            marginBottom: "15px",
          }}
        >
          {label} {required && <span style={{ color: "red" }}>*</span>}
        </MDBox>
      )}
      <Grid container spacing={2} sx={{ "@media (max-width: 768px)": { flexDirection: "column" } }}>
        <Grid
          item
          // xs={12}
          // md={12}
          sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
        >
          {hasUploaded || img ? (
            <Grid sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
              <MDBox sx={{ position: "relative" }}>
                <CloseIcon
                  onClick={handleImageRemove}
                  className="close_icon"
                  sx={{
                    position: "absolute",
                    top: `${hideLabel ? "10px" : "-12px"}`,
                    left: `${hideLabel ? "60px" : "252px"}`,
                    cursor: "pointer",
                    padding: "5px",
                    width: "25px",
                    height: "25px",
                    background: `${sideNav.backGround}`,
                    color: `${sideNav.textColor}`,
                    boxShadow: `${imageUploader.boxShadow}`,
                    borderRadius: "50px",
                    "&:hover": {
                      backgroundColor: `${sideNav?.hoverTextColor}`,
                      color: `${sideNav?.backGround}`,
                    },
                  }}
                />
              </MDBox>
              <Box
                component="img"
                className="image_preview"
                src={img || URL.createObjectURL(selectedFile)}
                alt="Selected"
                sx={{
                  width: `${hideLabel ? "80px" : "265px"}`,
                  height: `${hideLabel ? "80px" : "150px"}`,
                  borderRadius: `${hideLabel ? "100%" : "8px"}`,
                  marginTop: `${hideLabel ? "14px" : "0px"}`,
                  marginBottom: `${hideLabel ? "0px" : "15px"}`,
                }}
              />
            </Grid>
          ) : (
            <Grid container direction="column" alignItems="center">
              <label
                htmlFor={fileName}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <MDBox
                  sx={{
                    border: `2px dashed ${info.main}`,
                    marginTop: `${hideLabel ? "14px" : "0px"}`,
                    marginBottom: `${hideLabel ? "0px" : "0px"}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRadius: `${hideLabel ? "100%" : "16px"}`,
                    width: `${hideLabel ? "80px" : "250px"}`,
                    height: `${hideLabel ? "80px" : "150px"}`,
                    justifyContent: "center",
                    color: `${info.main}`,
                    textAlign: "center",
                    padding: "20px",
                    fontSize: `${hideLabel ? "12px" : "18px"}`,
                    lineHeight: `${hideLabel ? "14px" : "24px"}`,
                    cursor: "pointer",
                  }}
                  className="image_uploader"
                >
                  <CloudUploadIcon
                    sx={{
                      width: `${hideLabel ? "20px" : "30px"}`,
                      height: `${hideLabel ? "20px" : "30px"}`,
                      color: `${info.main}`,
                    }}
                  />
                  {hideLabel ? "Upload Image" : "Select an image file to upload"}
                </MDBox>
                <input
                  type="file"
                  id={fileName}
                  name={fileName}
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </label>

              {/* Validation message placed directly below the upload box */}
              {validationMessage && (
                <Box
                  sx={{
                    color: "red",
                    fontSize: "14px",
                    textAlign: "center",
                    marginTop: "8px",
                  }}
                >
                  {validationMessage}
                </Box>
              )}
            </Grid>
          )}
        </Grid>
        {!hideLabel && (
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "@media (max-width: 768px)": { paddingTop: "5px !important" },
            }}
          >
            <MDBox
              sx={{
                fontSize: "14px",
                width: "100%",
                paddingLeft: "5px",
                "@media (max-width: 768px)": {
                  display: "block",
                },
              }}
            >
              Image can't be larger than 5 MB
              <span
                style={{
                  marginTop: "2px",
                  display: "block",
                }}
              >
                (છબીનું કદ 5 mb કરતાં ઓછું હોવું જરૂરી છે)
              </span>
              {note && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "red",
                  }}
                >
                  Note: {note}
                </span>
              )}
            </MDBox>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

ImageUploader.propTypes = {
  sx: PropTypes.object,
  label: PropTypes.string,
  img: PropTypes.string,
  folderName: PropTypes.string,
  fileName: PropTypes.string,
  formik: PropTypes.any,
  hideLabel: PropTypes.bool,
  required: PropTypes.bool,
  name: PropTypes.string,
};

export default ImageUploader;
